import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Headphones as HeadphonesIcon,
  Favorite as HeartIcon,
  FavoriteBorder as HeartBorderIcon,
  VolumeUp as VolumeUpIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore'
import toast from 'react-hot-toast'

const ListeningWrite = ({ userData, onBack, selectedLevel }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [gameLoading, setGameLoading] = useState(false)
  const [currentLevel, setCurrentLevel] = useState(selectedLevel)
  const [userLevel, setUserLevel] = useState('A1')
  const [sentences, setSentences] = useState([])
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0)
  const [userAnswer, setUserAnswer] = useState('')
  const [lives, setLives] = useState(3)
  const [score, setScore] = useState(0)
  const [gameCompleted, setGameCompleted] = useState(false)
  const [showExitDialog, setShowExitDialog] = useState(false)
  const [showLevelCompleteDialog, setShowLevelCompleteDialog] = useState(false)
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false)
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false)

  useEffect(() => {
    if (user && userData && selectedLevel) {
      setUserLevel(userData.level || 'A1')
      setCurrentLevel(selectedLevel)
      loadSentences()
    }
  }, [user, userData, selectedLevel])

  useEffect(() => {
    if (selectedLevel) {
      setCurrentLevel(selectedLevel)
    }
  }, [selectedLevel])

  const loadSentences = async () => {
    try {
      setLoading(true)
      console.log('Loading words for level:', userLevel, 'game level:', selectedLevel)

      if (!selectedLevel) {
        console.error('selectedLevel is undefined')
        toast.error('Level seçimi yapılmadı')
        return
      }

      const firestoreLevel = userLevel.toUpperCase()

      const levelDocRef = doc(db, 'words', firestoreLevel, 'levels', selectedLevel.toString())
      console.log('Firebase path:', `words/${firestoreLevel}/levels/${selectedLevel}`)
      const levelDoc = await getDoc(levelDocRef)
      
      console.log('Level doc exists:', levelDoc.exists())
      console.log('Level doc data:', levelDoc.data())

      if (!levelDoc.exists()) {
        toast.error('Bu level için kelime bulunamadı')
        return
      }

      const data = levelDoc.data()
      const fieldName = firestoreLevel === 'EXAM' ? 'wordpairs' : 'wordPairs'
      const levelWords = data[fieldName] || []

      if (levelWords.length === 0) {
        toast.error('Bu level için kelime bulunamadı')
        return
      }

      console.log('Level words:', levelWords.length)
      setSentences(levelWords)
      startGame(levelWords)
    } catch (error) {
      console.error('Error loading words:', error)
      toast.error('Kelimeler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const startGame = (sentenceList) => {
    if (!sentenceList || sentenceList.length === 0) {
      toast.error('Cümle bulunamadı')
      return
    }

    setCurrentSentenceIndex(0)
    setLives(3)
    setScore(0)
    setGameCompleted(false)
    setUserAnswer('')
    setShowAnswerFeedback(false)
    setIsAnswerCorrect(false)
    
    // İlk kelimeyi otomatik olarak seslendir
    setTimeout(() => {
      if (sentenceList[0]?.word) {
        speakWord(sentenceList[0].word)
      }
    }, 500)
  }

  const handleSubmit = () => {
    if (gameLoading) return

    const currentWord = sentences[currentSentenceIndex]
    const isCorrect = userAnswer.toLowerCase().trim() === currentWord.word.toLowerCase().trim()

    // Cevap geri bildirimi göster
    setIsAnswerCorrect(isCorrect)
    setShowAnswerFeedback(true)

    if (isCorrect) {
      setScore(prev => prev + 10)
      
      // Sonraki kelimeye geç
      if (currentSentenceIndex + 1 < sentences.length) {
        setTimeout(() => {
          const nextIndex = currentSentenceIndex + 1
          setCurrentSentenceIndex(nextIndex)
          setUserAnswer('')
          setShowAnswerFeedback(false)
          
          // Sonraki kelimeyi otomatik olarak seslendir
          setTimeout(() => {
            if (sentences[nextIndex]?.word) {
              speakWord(sentences[nextIndex].word)
            }
          }, 200)
        }, 1500)
      } else {
        // Oyun tamamlandı
        setTimeout(() => {
          setGameCompleted(true)
          setShowLevelCompleteDialog(true)
          setShowAnswerFeedback(false)
        }, 1500)
      }
    } else {
      setLives(prev => prev - 1)
      setScore(prev => Math.max(0, prev - 5))
      
      // Can bitti mi kontrol et
      if (lives <= 1) {
        setTimeout(() => {
          setGameCompleted(true)
          setShowAnswerFeedback(false)
        }, 1500)
      } else {
        setTimeout(() => {
          setShowAnswerFeedback(false)
        }, 1500)
      }
    }

    setTimeout(() => {
      setGameLoading(false)
    }, 1500)
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSubmit()
    }
  }

  const completeLevel = async () => {
    setShowLevelCompleteDialog(false)
    
    try {
      const firestoreLevel = userLevel.toUpperCase()
      
      // /gameProgress/A1/listeningWrite/{userId}/completedLevels/level_{currentLevel}
      const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'listeningWrite', user.uid, 'completedLevels', `level_${currentLevel}`)
      
      await setDoc(levelDocRef, {
        completed: true,
        completedAt: new Date(),
        score: score
      })
      
      // Puanları da kaydet
      const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
      const userScoreDoc = await getDoc(userScoreRef)
      
      if (userScoreDoc.exists()) {
        const data = userScoreDoc.data()
        const completedLevels = data.completedLevels || []
        
        if (!completedLevels.includes(currentLevel)) {
          completedLevels.push(currentLevel)
        }
        
        await updateDoc(userScoreRef, {
          listeningWriteScore: increment(score),
          totalScore: increment(score),
          completedLevels: completedLevels
        })
      } else {
        await setDoc(userScoreRef, {
          username: userData?.username || user?.displayName || 'User',
          listeningWriteScore: score,
          totalScore: score,
          completedLevels: [currentLevel]
        })
      }
      
      
      
      console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
      
      toast.success(`Level ${currentLevel} tamamlandı! Skorunuz: ${score}`)
      
      // Bir sonraki level'e geç
      const nextLevelNumber = currentLevel + 1
      onBack({ page: 'listening-write', selectedLevel: nextLevelNumber })
    } catch (error) {
      console.error('Error saving score:', error)
      toast.error('Skor kaydedilirken hata oluştu')
      const nextLevelNumber = currentLevel + 1
      onBack({ page: 'listening-write', selectedLevel: nextLevelNumber })
         }
   }

   

  const restartGame = () => {
    setCurrentSentenceIndex(0)
    setLives(3)
    setScore(0)
    setGameCompleted(false)
    setUserAnswer('')
    setShowAnswerFeedback(false)
    setIsAnswerCorrect(false)
    
    // İlk kelimeyi otomatik olarak seslendir
    setTimeout(() => {
      if (sentences[0]?.word) {
        speakWord(sentences[0].word)
      }
    }, 500)
  }

  const exitGame = () => {
    setShowExitDialog(true)
  }

  const confirmExit = () => {
    setShowExitDialog(false)
    onBack()
  }

  const speakWord = (word) => {
    if (!word) return
    
    // Web Speech API kullanarak kelimeyi seslendir
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word)
      utterance.lang = 'en-US'
      utterance.rate = 0.8
      utterance.pitch = 1
      utterance.volume = 1
      
      // Mevcut konuşmayı durdur
      window.speechSynthesis.cancel()
      
      // Yeni kelimeyi seslendir
      window.speechSynthesis.speak(utterance)
    } else {
      toast.error('Ses desteği bulunamadı')
    }
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={exitGame}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              ListeningWrite
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={exitGame}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            ListeningWrite
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            Level {currentLevel}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Game Stats */}
        <Card sx={{ mb: 3, mx: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ mr: 2 }}>
                  Skor: {score}
                </Typography>
                                 <Typography variant="h6">
                   Kelime: {currentSentenceIndex + 1}/{sentences.length}
                 </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {[1, 2, 3].map((heart) => (
                  <Box key={heart} sx={{ ml: 0.5 }}>
                    {heart <= lives ? (
                      <HeartIcon sx={{ color: 'red', fontSize: 24 }} />
                    ) : (
                      <HeartBorderIcon sx={{ color: 'gray', fontSize: 24 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Game Content */}
        <Card sx={{ mx: 2, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                             <Typography variant="h5" sx={{ mb: 2 }}>
                 Kelime {currentSentenceIndex + 1}:
               </Typography>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                 <HeadphonesIcon sx={{ fontSize: 32, color: '#1976d2', mr: 1 }} />
                 <Typography variant="h6" sx={{ color: '#1976d2' }}>
                   Dinleyin ve yazın
                 </Typography>
               </Box>
                               <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                  {sentences[currentSentenceIndex]?.meaning || 'Kelimeyi dinleyin ve İngilizce olarak yazın'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<VolumeUpIcon />}
                    onClick={() => speakWord(sentences[currentSentenceIndex]?.word)}
                    sx={{ 
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: '#f3f8ff'
                      }
                    }}
                  >
                    Dinle
                  </Button>
                </Box>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
                             <TextField
                 fullWidth
                 label="Kelimeyi yazın"
                 value={userAnswer}
                 onChange={(e) => setUserAnswer(e.target.value)}
                 onKeyPress={handleKeyPress}
                 disabled={gameCompleted}
                 sx={{ 
                   mb: 2,
                   '& .MuiOutlinedInput-root': {
                     borderColor: showAnswerFeedback 
                       ? (isAnswerCorrect ? '#4caf50' : '#f44336')
                       : '#e0e0e0',
                     backgroundColor: showAnswerFeedback 
                       ? (isAnswerCorrect ? '#e8f5e8' : '#ffebee')
                       : '#ffffff'
                   }
                 }}
               />
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={!userAnswer.trim() || gameCompleted}
                sx={{ minWidth: 120 }}
              >
                Kontrol Et
              </Button>
            </Box>

            {showAnswerFeedback && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: isAnswerCorrect ? '#4caf50' : '#f44336',
                    fontWeight: 'bold'
                  }}
                >
                  {isAnswerCorrect ? '✅ Doğru!' : '❌ Yanlış!'}
                </Typography>
                                 {!isAnswerCorrect && (
                   <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                     Doğru cevap: {sentences[currentSentenceIndex]?.word}
                   </Typography>
                 )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Level Complete Dialog */}
        <Dialog open={showLevelCompleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            🏆 Level {currentLevel} Tamamlandı!
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Final Skor: {score}
              </Typography>
                             <Typography variant="body1" sx={{ mb: 2 }}>
                 Tebrikler! Level {currentLevel}'deki tüm kelimeleri başarıyla yazdınız.
               </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              variant="outlined" 
              onClick={async () => {
                // Level'i ve puanları kaydet ve sonra geri dön
                try {
                  const firestoreLevel = userLevel.toUpperCase()
                  const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'listeningWrite', user.uid, 'completedLevels', `level_${currentLevel}`)
                  
                  await setDoc(levelDocRef, {
                    completed: true,
                    completedAt: new Date(),
                    score: score
                  })
                  
                  // Puanları da kaydet
                  const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
                  const userScoreDoc = await getDoc(userScoreRef)
                  
                  if (userScoreDoc.exists()) {
                    const data = userScoreDoc.data()
                    const completedLevels = data.completedLevels || []
                    
                    if (!completedLevels.includes(currentLevel)) {
                      completedLevels.push(currentLevel)
                    }
                    
                    await updateDoc(userScoreRef, {
                      listeningWriteScore: increment(score),
                      totalScore: increment(score),
                      completedLevels: completedLevels
                    })
                  } else {
                    await setDoc(userScoreRef, {
                      username: userData?.username || user?.displayName || 'User',
                      listeningWriteScore: score,
                      totalScore: score,
                      completedLevels: [currentLevel]
                    })
                  }
                  
                  console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
                  toast.success(`Level ${currentLevel} tamamlandı! Puanınız: ${score}`)
                } catch (error) {
                  console.error('Error saving score:', error)
                  toast.error('Skor kaydedilirken hata oluştu')
                }
                // Level seçim sayfasına dön
                onBack({ page: 'listening-write-level-selection', userLevel: userLevel })
              }}
              sx={{ mr: 2 }}
            >
              Level Seçimi
            </Button>
            <Button 
              variant="contained" 
              onClick={completeLevel}
            >
              Sonraki Level
            </Button>
          </DialogActions>
        </Dialog>

        {/* Game Over Dialog */}
        <Dialog open={gameCompleted && !showLevelCompleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {lives > 0 ? '🎉 Oyun Tamamlandı!' : '😔 Oyun Bitti'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Skorunuz: {score}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                                 {lives > 0 
                   ? `Tebrikler! ${sentences.length} kelimeyi başarıyla yazdınız.`
                   : 'Canlarınız tükendi. Tekrar deneyin!'
                 }
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              variant="outlined" 
              onClick={async () => {
                // Eğer oyun başarıyla tamamlandıysa level'i ve puanları kaydet
                if (lives > 0) {
                  try {
                    const firestoreLevel = userLevel.toUpperCase()
                    const levelDocRef = doc(db, 'gameProgress', firestoreLevel, 'listeningWrite', user.uid, 'completedLevels', `level_${currentLevel}`)
                    
                    await setDoc(levelDocRef, {
                      completed: true,
                      completedAt: new Date(),
                      score: score
                    })
                    
                    // Puanları da kaydet
                    const userScoreRef = doc(db, 'levels', firestoreLevel, 'leaderboard', user.uid)
                    const userScoreDoc = await getDoc(userScoreRef)
                    
                    if (userScoreDoc.exists()) {
                      const data = userScoreDoc.data()
                      const completedLevels = data.completedLevels || []
                      
                      if (!completedLevels.includes(currentLevel)) {
                        completedLevels.push(currentLevel)
                      }
                      
                      await updateDoc(userScoreRef, {
                        listeningWriteScore: increment(score),
                        totalScore: increment(score),
                        completedLevels: completedLevels
                      })
                    } else {
                      await setDoc(userScoreRef, {
                        username: userData?.username || user?.displayName || 'User',
                        listeningWriteScore: score,
                        totalScore: score,
                        completedLevels: [currentLevel]
                      })
                    }
                    
                    console.log(`Level ${currentLevel} completed and saved to gameProgress collection with score: ${score}`)
                    toast.success(`Level ${currentLevel} tamamlandı! Puanınız: ${score}`)
                  } catch (error) {
                    console.error('Error saving score:', error)
                    toast.error('Skor kaydedilirken hata oluştu')
                  }
                }
                // Level seçim sayfasına dön
                onBack({ page: 'listening-write-level-selection', userLevel: userLevel })
              }}
              sx={{ mr: 2 }}
            >
              Level Seçimi
            </Button>
            <Button 
              variant="contained" 
              onClick={restartGame}
            >
              Tekrar Oyna
            </Button>
          </DialogActions>
        </Dialog>

        {/* Exit Confirmation Dialog */}
        <Dialog open={showExitDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Oyundan Çık</DialogTitle>
          <DialogContent>
            <Typography>
              Oyundan çıkmak istediğinizden emin misiniz? İlerlemeniz kaydedilmeyecek.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExitDialog(false)}>
              İptal
            </Button>
            <Button onClick={confirmExit} color="error">
              Çık
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

export default ListeningWrite
